// src/components/PaymentForm.jsx
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faDownload } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import { api } from '../utilities/api';
import Payment from './Payment';
import Spinner from './Spinner';
import { useUser } from '../contexts/UserContext';
import { useOrder } from '../contexts/OrderContext';
import useModalA11y from '../hooks/useModalA11y';
import { parseCents, formatCents } from '../utilities/cents';
import { findProductByCode, formatDispenseQuantity } from '../utilities/productDisplay';
import TrustBadges from './TrustBadges';

// Updated PaymentForm to handle receipt data

function PaymentForm({
  user,
  order,
  appointment,
  appointmentId,
  onSuccess,
  onCancel,
  onPaymentActivityChange,
  actionMode = false,
  // Support for receipt display
  showReceipt = false,
  receiptData = { pdfUrl: null, pdfFile: null }
}) {
  const { updateUserInfo } = useUser();
  const { products: orderProducts } = useOrder() || {};
  // orders.* doesn't carry route/dispense facets; look the product up by
  // product_code from the authenticated product list to get them.
  const orderQuantityLabel = order?.product_code
    ? formatDispenseQuantity(findProductByCode(orderProducts, order.product_code), order?.order_qty)
    : null;
  const [processingError, setProcessingError] = useState(false);
  const [paymentIssues, setPaymentIssues] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showError, setShowError] = useState(false);
  const [paymentComponentKey, setPaymentComponentKey] = useState(Date.now());
	const [showPayPal] = useState(false);

  const [addressToUse, setAddressToUse] = useState({});
  const [shippingAddress, setShippingAddress] = useState({});
  const [payPalOrder, setPayPalOrder] = useState({});

  // ——————————————
  // for verification / modal
  // ——————————————
  const [verifiedToUse, setVerifiedToUse]             = useState(null);
  const [verifiedShipping, setVerifiedShipping]       = useState(null);
  const [showVerifyModal, setShowVerifyModal]         = useState(false);
  const [useVerifiedToUse, setUseVerifiedToUse]       = useState(false);
  const [useVerifiedShipping, setUseVerifiedShipping] = useState(false);
  const [addressVerified, setAddressVerified]         = useState(false);
  const verifyModalRef = useRef(null);

  // Determine if this is an order or appointment payment
  const mode = order ? "order" : "appointment";
  const patientFirstName = user?.first_name || user?.firstname || '';
  const patientLastName = user?.last_name || user?.lastname || '';

 // — map **from** PayPal → USPS shape for the API call —
  const toUSPS = ({ address_line_1, address_line_2,
                    admin_area_2, admin_area_1,
                    postal_code }) => ({
    address1: address_line_1,
    address2: address_line_2,
    city:     admin_area_2,
    state:    admin_area_1,
    zip:      postal_code,
  });

  // — map **from** USPS result → PayPal shape for your form —
  const toPayPal = ({ address, address2, city, state, zip }) => ({
    address_line_1: address,
    address_line_2: address2 || null,
    admin_area_2:   city,
    admin_area_1:   state,
    postal_code:    zip,
    country_code:   'US'
  });

  // ——————— sanitize phone helper ———————
  const sanitizedPhoneNumber = () => {
    const raw = user?.phone_mobile || user?.phone_home || user?.phone_work || user?.phone_work_home || '';
    return raw.replace(/\D/g, '');
  };

  // ——————— validate phone helper ———————
  const validatePhoneNumber = () => {
    const phone = sanitizedPhoneNumber();
    // US phone numbers should be 10 digits
    return phone && phone.length >= 10;
  };

  const validatePaymentProfile = (address) => {
    if (!address?.address_line_1?.trim()) {
      return 'Update address and phone before paying. Your billing address is missing.';
    }

    if (!address?.admin_area_2?.trim() || !address?.admin_area_1?.trim() || !address?.postal_code?.trim()) {
      return 'Update address and phone before paying. Your billing address must include city, state, and zip code.';
    }

    if (!validatePhoneNumber()) {
      return 'Update address and phone before paying. A valid 10-digit phone number is required.';
    }

    return null;
  };

  const getAddressIssue = (address, label) => {
    if (!address?.address_line_1?.trim()) {
      return `${label} address is missing.`;
    }

    if (!address?.admin_area_2?.trim() || !address?.admin_area_1?.trim() || !address?.postal_code?.trim()) {
      return `${label} address must include city, state, and zip code.`;
    }

    return null;
  };

  const getPaymentPrerequisiteIssues = ({ billingAddress, shipAddress, customId }) => {
    const issues = [];
    const cleanPhone = sanitizedPhoneNumber();

    if (!user?.userid) issues.push({ type: 'profile', message: 'User account details are missing.' });
    if (!user?.email) issues.push({ type: 'profile', message: 'Email address is missing.' });
    if (!patientFirstName || !patientLastName) issues.push({ type: 'profile', message: 'Patient first and last name are required.' });
    if (!cleanPhone || cleanPhone.length < 10) issues.push({ type: 'profile', message: 'A valid 10-digit phone number is required.' });

    const billingIssue = getAddressIssue(billingAddress, 'Billing');
    if (billingIssue) issues.push({ type: 'profile', message: billingIssue });

    if (isOrder) {
      if (!order?.order_id) issues.push({ type: 'order', message: 'Order number is missing.' });
      if (!Number.isFinite(Number(order?.order_net)) || Number(order?.order_net) < 0) {
        issues.push({ type: 'order', message: 'Order subtotal is missing or invalid.' });
      }

      const shippingIssue = getAddressIssue(shipAddress, 'Shipping');
      if (shippingIssue) issues.push({ type: 'profile', message: shippingIssue });
    } else if (appointment) {
      if (!appointment?.doctor_user_id || !appointment?.date || !appointment?.time) {
        issues.push({ type: 'appointment', message: 'Appointment details are incomplete.' });
      }
    }

    if (!customId || customId === 'unknownId') {
      issues.push({ type: isOrder ? 'order' : 'appointment', message: isOrder ? 'Order payment identifier is missing.' : 'Appointment payment identifier is missing.' });
    }

    return issues;
  };

  const setPrerequisiteError = (issues) => {
    const message = issues.map(issue => issue.message).join(' ');
    setPaymentIssues(issues);
    setShowError(true);
    setProcessingError(message || 'Payment information is incomplete.');
    setPayPalOrder({});
    toast.error(issues[0]?.message || 'Payment information is incomplete.');
  };

  const buildPayPalOrder = ({ payer, billingAddress, shipAddress, customId }) => {
    const issues = getPaymentPrerequisiteIssues({ billingAddress, shipAddress, customId });
    if (issues.length > 0) {
      setPrerequisiteError(issues);
      return null;
    }

    setPaymentIssues([]);
    setShowError(false);
    setProcessingError(false);

    return {
      payer: {
        ...payer,
        address: billingAddress
      },
      ...(order ? { order } : {}),
      ...(appointment ? { appointment } : {}),
      purchase_units: [{
        custom_id: customId,
        shipping: { address: shipAddress }
      }]
    };
  };

  const sameInitial =
    JSON.stringify(shippingAddress) ===
    JSON.stringify(addressToUse);

  // decide what to show for shipping
  // either the real verifiedShipping, or if none but sameInitial & billing was corrected,
  // use the billing correction as the shipping suggestion
  const shippingSuggestion =
    verifiedShipping ||
    (sameInitial && verifiedToUse);

  useEffect(() => {
    // Check if user data is available and complete
    if (!user || !user.userid) {
      return;
    }

    // — initial PayPal‐format addresses —
    const initialShipping = {
      address_line_1: user?.address1    || '',
      address_line_2: user?.address2    || null,
      admin_area_2:   user?.city        || '',
      admin_area_1:   user?.state       || '',
      postal_code:    user?.postal_code || '',
      country_code:   'US'
    };

    const initialToUse = user?.billing_address1
      ? {
          address_line_1: user?.billing_address1,
          address_line_2: user?.billing_address2 || null,
          admin_area_2:   user?.billing_city,
          admin_area_1:   user?.billing_state,
          postal_code:    user?.billing_postal_code,
          country_code:   'US'
        }
      : {
          ...initialShipping,
          country_code:   'US'
        };

    // Set the addresses first, so we at least have something to work with
    setShippingAddress(initialShipping);
    setAddressToUse(initialToUse);

    // Receipt mode (post-payment): payment already captured. Just populate
    // addresses for the receipt UI; do not re-validate or build a new PayPal
    // order. PaymentForm unmounts during isProcessing and remounts with empty
    // address state, so finalizePayPalOrder() here would falsely report
    // "billing address is missing".
    if (showReceipt) {
      return;
    }

    const validationMessage = !isProcessing ? validatePaymentProfile(initialToUse) : null;
    if (validationMessage) {
      setShowError(true);
      setProcessingError(validationMessage);
      setPayPalOrder({});
      toast.error(validationMessage);

      return;
    }

    setShowError(false);
    setProcessingError(false);

    (async () => {
      // Skip address verification if we've already verified and updated the address
      if (addressVerified) {
        // Create the PayPal order with the current addresses
        const payer = {
          email_address: user?.email,
          name: {
            given_name: user?.first_name,
            surname: user?.last_name,
          },
          address: addressToUse,
          phone: {
            phone_type: user?.phone_mobile ? 'MOBILE' : user?.phone_home ? 'HOME' : 'OTHER',
            phone_number: {
              national_number: sanitizedPhoneNumber().replace(/\D/g, '')
            }
          }
        };

        // Use the server-assigned pending appointment_id when available (Phase 2
        // reserve-then-pay flow). Falls back to the legacy composite key for any
        // path that doesn't yet pass appointmentId (e.g. direct pay on existing appt).
        const customId = order ? order.order_id : (appointmentId ? `appt-${appointmentId}` : `appt-${appointment?.doctor_user_id}-${appointment?.date}-${appointment?.time}`);
        const nextPayPalOrder = buildPayPalOrder({
          payer,
          billingAddress: addressToUse,
          shipAddress: shippingAddress,
          customId,
        });
        if (nextPayPalOrder) setPayPalOrder(nextPayPalOrder);

        return; // Exit early
      }

      async function safeVerify(uspsAddr) {
        try {
          const res = await api.verifyAddress(uspsAddr);
          if (!res || res.success !== true) {
            return { differences: false };
          }
          return res;
        } catch {
          return { differences: false };
        }
      }

      // An action link is deliberately isolated from any existing full
      // UserContext session. Do not send the logged-in user's Bearer token to
      // address/profile endpoints while paying another capability-bound order.
      const resToUse = actionMode
        ? { differences: false }
        : await safeVerify(toUSPS(initialToUse));

      const resShip = actionMode
        ? { differences: false }
        : JSON.stringify(initialShipping) !== JSON.stringify(initialToUse)
          ? await safeVerify(toUSPS(initialShipping))
          : { differences: false };

      // Process the verification results
      if (resToUse.differences || resShip.differences) {
        // There are differences, so we need to show the verification modal
        if (resToUse.differences) {
          setVerifiedToUse(toPayPal(resToUse));
        }
        if (resShip.differences) {
          setVerifiedShipping(toPayPal(resShip));
        }
        setShowVerifyModal(true);
      } else {
        // No differences found, proceed with the initial addresses
        // Mark as verified since no changes were needed
        setAddressVerified(true);

        // Create the PayPal order directly with the initial addresses
        const payer = {
          email_address: user?.email,
          name: {
            given_name: user?.first_name,
            surname: user?.last_name,
          },
          address: initialToUse,
          phone: {
            phone_type: user?.phone_mobile ? 'MOBILE' : user?.phone_home ? 'HOME' : 'OTHER',
            phone_number: {
              national_number: sanitizedPhoneNumber().replace(/\D/g, '')
            }
          }
        };

        // Ensure country codes are set to US
        const finalAddr = { ...initialToUse };
        const finalShip = { ...initialShipping };

        if (finalAddr.country_code !== 'US') {
          finalAddr.country_code = 'US';
        }

        if (finalShip.country_code !== 'US') {
          finalShip.country_code = 'US';
        }

        // Use the server-assigned pending appointment_id when available (Phase 2
        // reserve-then-pay flow). Falls back to the legacy composite key for any
        // path that doesn't yet pass appointmentId (e.g. direct pay on existing appt).
        const customId = order ? order.order_id : (appointmentId ? `appt-${appointmentId}` : `appt-${appointment?.doctor_user_id}-${appointment?.date}-${appointment?.time}`);
        const nextPayPalOrder = buildPayPalOrder({
          payer,
          billingAddress: finalAddr,
          shipAddress: finalShip,
          customId,
        });
        if (nextPayPalOrder) setPayPalOrder(nextPayPalOrder);

        // No need to call finalizePayPalOrder() as we've already set the PayPal order
      }

    })();
  }, [user?.userid, order?.order_id, appointment?.appointment_id, addressVerified, actionMode]);

  // The order can gain its provider / quote after the address setup above has
  // already built payPalOrder (a detail refetch lands later, same order_id).
  // Fold the newer order in without redoing address setup. Depends only on the
  // quote fields, and writing payPalOrder does not change them: no loop.
  useEffect(() => {
    if (!order) return;
    setPayPalOrder((prev) => (
      prev?.order && String(prev.order.order_id) === String(order.order_id) && prev.order !== order
        ? { ...prev, order }
        : prev
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.provider, order?.total_cents, order?.surcharge_cents]);

  // ——————— build final PayPal order ———————
  const finalizePayPalOrder = () => {
    // If we have verified addresses and user chose to use them, use those
    // Otherwise fall back to the current addressToUse/shippingAddress values
    // which could be either the initial values or user-modified values
    const finalAddr = useVerifiedToUse && verifiedToUse
      ? verifiedToUse
      : addressToUse;

    const finalShip = useVerifiedShipping && shippingSuggestion
      ? shippingSuggestion
      : shippingAddress;

    const profileError = validatePaymentProfile(finalAddr);
    if (profileError) {
      setShowError(true);
      setProcessingError(profileError);
      setPayPalOrder({});
      toast.error(profileError);
      return;
    }

    const phoneVal = sanitizedPhoneNumber();
    const phoneType = user?.phone_mobile ? 'MOBILE' : user?.phone_home ? 'HOME' : 'OTHER';

    const billingAddress = { ...finalAddr, country_code: 'US' };
    const shipAddress = { ...finalShip, country_code: 'US' };

    const payer = {
      email_address: user?.email,
      name: {
        given_name: user?.first_name,
        surname:    user?.last_name,
      },
      address: billingAddress,
      phone: {
        phone_type: phoneType,
        phone_number: {
          national_number: phoneVal.replace(/\D/g, '')
        }
      }
    };

    const customId = order ? order.order_id : `appt-${appointment?.doctor_user_id}-${appointment?.date}-${appointment?.time}`;
    const nextPayPalOrder = buildPayPalOrder({
      payer,
      billingAddress,
      shipAddress,
      customId,
    });
    if (nextPayPalOrder) setPayPalOrder(nextPayPalOrder);
  };

  // called when user clicks "Confirm" in modal
  const handleModalConfirm = async () => {
    // Set local state for the form
    if (useVerifiedToUse && verifiedToUse) {
      setAddressToUse(verifiedToUse);
    }
    if (useVerifiedShipping && shippingSuggestion) {
      setShippingAddress(shippingSuggestion);
    }

    // Update user profile with verified addresses if selected
    if (!actionMode && ((useVerifiedToUse && verifiedToUse) || (useVerifiedShipping && shippingSuggestion))) {

      try {
        // Prepare the data to update in user profile
        const updateData = {};

        // Add billing address if verified and selected
        if (useVerifiedToUse && verifiedToUse) {
          // verifiedToUse is already in PayPal format, so we can use it directly
          updateData.billing_address1 = verifiedToUse.address_line_1;
          updateData.billing_address2 = verifiedToUse.address_line_2 || '';
          updateData.billing_city = verifiedToUse.admin_area_2;
          updateData.billing_state = verifiedToUse.admin_area_1;
          updateData.billing_postal_code = verifiedToUse.postal_code;
        }

        // Add shipping address if verified and selected
        if (useVerifiedShipping && shippingSuggestion) {
          // shippingSuggestion is already in PayPal format, so we can use it directly
          updateData.address1 = shippingSuggestion.address_line_1;
          updateData.address2 = shippingSuggestion.address_line_2 || '';
          updateData.city = shippingSuggestion.admin_area_2;
          updateData.state = shippingSuggestion.admin_area_1;
          updateData.postal_code = shippingSuggestion.postal_code;
        }

        // Only update if we have data to update
        if (Object.keys(updateData).length > 0) {
          const result = await updateUserInfo(updateData);

          if (result && result.success) {
            toast.success("Your profile has been updated with the verified address");
          } else {
            toast.error("Could not update your profile with the verified address");
          }
        }
      } catch {
        toast.error("Error updating your profile with the verified address");
      }
    }

    // Mark address as verified to prevent re-verification loop
    setAddressVerified(true);
    setShowVerifyModal(false);
    finalizePayPalOrder();
  };

  const handleOnSuccess = (payPalData) => {
    setShowError(false);
    setProcessingError(false);

    // Pass the PayPal data to the parent component
    if (onSuccess) {
      setIsProcessing(true);
      try {
        onSuccess(payPalData);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleOnError = (err) => {
    // If it's a user-initiated cancellation
    if (err?.cancelled) {
      // Show toast message here since PaymentForm is the common component
      toast.error("Payment cancelled");

      // Force recreation of the Payment component by changing its key
      setPaymentComponentKey(Date.now());

      // Inform parent if needed, but only for navigation purposes
      if (onCancel) {
        onCancel({
          type: 'cancelled',
          originalError: err
        });
      }
      return;
    }

    // For actual errors
    setShowError(true);

    // Extract a user-friendly error message
    let errorMessage = "";

    // Check if this is our enhanced error format from Payment.jsx
    if (err?.message) {
      errorMessage = err.message;
      setProcessingError(errorMessage);
    } else if (typeof err === 'string') {
      errorMessage = `Payment failed. Please retry. ${err}`;
      setProcessingError(errorMessage);
    } else {
      // Try to extract error information from PayPal API error format
      try {
        errorMessage = "Payment failed. Please try again.";

        // Check if we have a PayPal API error response
        if (err?.body) {
          const body = typeof err.body === 'string' ? JSON.parse(err.body) : err.body;

          if (body.message) {
            errorMessage = body.message;
          }

          // Check for field-specific errors
          if (body.details && Array.isArray(body.details) && body.details.length > 0) {
            // Look for specific issues we want to handle
            const phoneIssues = body.details.filter(detail =>
              detail.field?.includes('phone') || detail.field?.includes('national_number')
            );

            if (phoneIssues.length > 0) {
              errorMessage = "Your phone number is missing or invalid. Please update your profile with a valid phone number.";
            } else {
              // Generic field error
              const fieldName = body.details[0].field?.split('/').pop() || 'field';
              errorMessage = `Payment error: Invalid ${fieldName}. Please check your information.`;
            }
          }
        }

        setProcessingError(errorMessage);
      } catch {
        errorMessage = "Payment failed. Please check your information and try again.";
        setProcessingError(errorMessage);
      }
    }

    // No toast here: the "Payment Error" box below (showError/processingError)
    // renders this exact same message. A toast plus a persistent box is
    // double-noise for the same failure - the box wins. (The cancellation
    // toast above, and the other pre-submit validation toasts elsewhere in
    // this file, are untouched - this only covers actual payment failures.)

    // Inform parent if needed, but only for navigation purposes
    if (onCancel) {
      onCancel({
        type: 'error',
        originalError: err
      });
    }
  };

  const handleViewPDF = (base64Data) => {
    if (!base64Data) return;

    // Strip the data prefix if present
    const base64 = base64Data.split(',')[1];
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);

    // Open in a new tab
    const win = window.open(blobUrl, '_blank');
    if (!win) {
      toast.error('Popup blocked. Please allow popups for this site.');
    }

    // Optionally revoke the blob URL after a delay
    setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
  };

  // Calculate formatted prices for display
  const isOrder = mode === "order";

  // Base amount (order_net for orders, copay_amount for appointments)
  const amountString = isOrder
    ? (parseFloat(order?.order_net || 0)).toFixed(2)
    : (parseFloat(appointment?.copay_amount || 0)).toFixed(2);

  // Shipping amount (only for orders)
  const shippingString = isOrder
    ? (parseFloat(order?.order_shipping || 0)).toFixed(2)
    : "0.00";

  // The service fee is NOT computed here. It is whatever the server says it is.
  //
  // This used to be `order_net * 0.035`, which duplicated the processing rate
  // into this repo, in a second language, on a separate deploy cadence - so no
  // server-side change could reach it. The rate is now per-provider and
  // administrator-editable (PayPal is flat 3.5%, the bank account is $0.30 +
  // 2.5%, and both are renegotiated at volume benchmarks), and any number
  // computed here would disagree with the one the server verifies the payment
  // against. The order payload carries surcharge_cents and total_cents.

  // The server returns nulls when it cannot price the order - a fee schedule
  // that exists but is misconfigured. It refuses rather than quote a rate
  // nobody set, and so must this screen: showing a wrong total, or handing one
  // to PayPal, is worse than showing none.
  const canQuote = !isOrder || (
    parseCents(order?.surcharge_cents) !== null
    && parseCents(order?.total_cents) !== null
  );

  const serviceFee = isOrder && canQuote
    ? formatCents(order.surcharge_cents)
    : "0.00"; // Appointments carry no service fee.

  const totalString = isOrder
    ? (canQuote ? formatCents(order.total_cents) : "")
    : (parseFloat(appointment?.copay_amount || 0)).toFixed(2);

  const renderOrderSummary = () => {
    if (!canQuote) {
      return (
        <div className="flex flex-col w-full max-w-3xl mx-auto bg-gray-50 p-6 rounded border border-gray-200 mb-4 text-sm">
          <h3 className="text-md font-semibold text-gray-700 mb-2">Order Summary</h3>
          <p className="text-gray-700">
            We cannot show the amount for this order right now, so there is nothing to pay yet.
            You have not been charged. Please try again shortly, or call the office.
          </p>
        </div>
      );
    }

    return (
      <div className={`flex flex-col w-full max-w-3xl mx-auto bg-gray-50 p-6 rounded border border-gray-200 mb-4 text-sm space-y-3 ${showPayPal ? 'hidden md:flex' : 'flex'}`}>
        <h3 className="text-md font-semibold text-gray-700 mb-2">
          {isOrder ? "Order Summary" : "Appointment Copay"}
        </h3>

        {isOrder ? (
          <div>
            <p><span className="font-semibold text-gray-600">Order ID:</span> {order?.order_id}</p>
            <p><span className="font-semibold text-gray-600">Status:</span> {order?.order_status}</p>
            <p><span className="font-semibold text-gray-600">Date:</span> {order?.order_date ? new Date(order.order_date).toLocaleDateString() : ''}</p>
            <p><span className="font-semibold text-gray-600">Item:</span> {order?.product_name} ({order?.dosage}) {order?.order_qty} month{order?.order_qty !== '1' ? 's' : ''}{orderQuantityLabel ? ` — ${orderQuantityLabel}` : ''}</p>
          </div>
        ) : (
          <div>
            {appointment?.appointment_id && (
              <p><span className="font-semibold text-gray-600">Appointment ID:</span> {appointment.appointment_id}</p>
            )}
            <p><span className="font-semibold text-gray-600">Date:</span> {appointment?.date ? new Date(appointment.date).toLocaleDateString() : 'TBD'}</p>
            <p><span className="font-semibold text-gray-600">Time:</span> {appointment?.time || 'TBD'}</p>
            <p><span className="font-semibold text-gray-600">Provider:</span> {appointment?.provider_name || 'Selected Provider'}</p>
            <p><span className="font-semibold text-gray-600">Service:</span> {appointment?.service_name || 'Telemedicine Appointment'}</p>
          </div>
        )}

        <div>
          <p><span className="font-semibold text-gray-600">Patient:</span> {patientFirstName} {patientLastName}</p>
          <p><span className="font-semibold text-gray-600">Email:</span> {user?.email}</p>
          <p><span className="font-semibold text-gray-600">Phone:</span> {user?.phone_mobile || user?.phone_home}</p>
        </div>
        <div className="text-red-600 text-center">
          <p>
            {actionMode
              ? 'Please carefully verify the payment and shipping details below. Sign in separately if account details need to be updated.'
              : 'Please carefully verify your addresses below. Click the profile button above to make changes.'}
          </p>

        </div>
        <div className="flex flex-row justify-start space-x-8">
          <div>
            <p className="font-semibold text-gray-600">Billing Address:</p>
            <p>{addressToUse.address_line_1} {addressToUse.address_line_2}</p>
            <p>{addressToUse.admin_area_2}, {addressToUse.admin_area_1} {addressToUse.postal_code}</p>
          </div>

          <div>
            <p className="font-semibold text-gray-600">Shipping Address:</p>
            <p>{shippingAddress.address_line_1} {shippingAddress.address_line_2}</p>
            <p>{shippingAddress.admin_area_2}, {shippingAddress.admin_area_1} {shippingAddress.postal_code}</p>
          </div>
        </div>

        <div className="border-t border-gray-300 pt-3">
          {isOrder && (
            <>
              <p className="text-md text-gray-800 font-semibold">Subtotal: <span className="float-right">${amountString}</span></p>
              <p className="text-md text-gray-800 font-semibold">Service fee: <span className="float-right">${serviceFee}</span></p>
              <p className="text-md text-gray-800 font-semibold">Shipping: <span className="float-right">${shippingString}</span></p>
            </>
          )}
          <p className="text-lg text-black font-bold border-t border-gray-300 mt-2 pt-2">
            {isOrder ? "Total:" : "Copay Amount:"} <span className="float-right">${totalString}</span>
          </p>
        </div>

        {isOrder && order?.order_status?.toLowerCase() === 'pending' && (
          <div className="mt-4 p-3 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 rounded text-sm">
            <p><strong>Payment Pending:</strong> Please complete your payment to finalize this order.</p>
          </div>
        )}

        {!isOrder && !appointment?.appointment_id && (
          <div className="mt-4 p-3 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 rounded text-sm">
            <p><strong>Copay Required:</strong> Please complete your copay payment to book your appointment.</p>
          </div>
        )}
      </div>
    );
  };

  const renderPayment = () => {
    if (!payPalOrder?.payer) {
      return null;
    }
    // No priced total means no payment control. Rendering one would hand the
    // processor an amount this client invented.
    if (!canQuote) {
      return null;
    }

    return (
      <div id="renderPayment" className="mb-6 w-full flex flex-col flex-grow items-center max-w-3xl mx-auto">
        <Payment
          key={paymentComponentKey} // This key forces recreation when changed
          description={isOrder
            ? `Order: ${order?.order_id} - ${order?.product_name} ${order?.dosage} ${order?.order_qty} month${order?.order_qty !== '1' ? 's' : ''}${orderQuantityLabel ? ` (${orderQuantityLabel})` : ''}`
            : `Copay for appointment on ${appointment?.date ? appointment.date : 'upcoming appointment'}`
          }
          value={totalString}
          currency="USD"
          orderid={payPalOrder?.purchase_units?.[0]?.custom_id || (isOrder ? order?.order_id : (appointmentId || appointment?.appointment_id))}
          payPalOrder={payPalOrder}
          shippingAddress={shippingAddress}
          appointmentId={appointmentId}
          onSuccess={handleOnSuccess}
          onError={handleOnError}
          onPaymentActivityChange={onPaymentActivityChange}
        />
      </div>
    );
  };

  const renderReceipt = () => {
    return (
        <div
          // className="flex-grow flex-shrink bg-white rounded-lg shadow-2xl p-6 w-full max-w-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="text-xl text-center text-primary border-t-2 pt-4 border-b-primary-light">Transaction Complete</h2>
          <div className="flex flex-grow w-max flex-col items-center gap-2 mt-4 flex-wrap">
            <p className="text-button">
              Your purchase is done! Get your receipt below or from your dashboard anytime.
            </p>
            <div className="w-full flex flex-grow flex-wrap gap-3 mt-6 justify-center">
              {receiptData?.pdfFile && (<button
                onClick={() => handleViewPDF(receiptData.pdfFile)}
                className="w-1/2 md:w-1/3 bg-button text-white px-2 py-2 rounded hover:bg-button-light"
              >
                View PDF Receipt <FontAwesomeIcon icon={faFilePdf} />
              </button>
              )}
              {receiptData?.pdfUrl && (<a
                href={receiptData.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-1/2 md:w-1/3 bg-button text-white px-2 py-2 rounded hover:bg-button-light cursor-pointer text-center "
              >
                View PDF Receipt <FontAwesomeIcon icon={faFilePdf} />
              </a>
						)}
            </div>
          </div>
        </div>
    );
  };

  useModalA11y({
    isOpen: showVerifyModal,
    dialogRef: verifyModalRef,
    onClose: () => setShowVerifyModal(false),
  });

  const renderVerifyModal = () => {
    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      >
        <div
          ref={verifyModalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="addressVerificationTitle"
          tabIndex={-1}
          className="bg-white rounded-lg shadow-xl p-6 w-full max-w-max md:max-w-max"
          onClick={e => e.stopPropagation()}
        >
          <h2 id="addressVerificationTitle" className="text-xl font-bold mb-4">Address Verification</h2>
          <p className="my-4 text-gray-700">
            USPS has suggested standardized versions of your address(es).
            Please review and choose which to apply.
          </p>

          {verifiedToUse && (
            <div className="mb-6">
              <h4 className="font-semibold mb-2">Primary Address</h4>
              <div className="grid grid-cols-2 gap-4">
                {/* Original */}
                <div className="p-3 border rounded">
                  <p className="text-gray-600 text-sm">Original</p>
                  <p>
                    {addressToUse.address_line_1}
                    {addressToUse.address_line_2 ? ` ${addressToUse.address_line_2}` : ''}
                  </p>
                  <p>
                    {addressToUse.admin_area_2}, {addressToUse.admin_area_1}{' '}
                    {addressToUse.postal_code}
                  </p>
                </div>
                {/* Suggested */}
                <div className="p-3 border rounded">
                  <p className="text-gray-600 text-sm">USPS Suggested</p>
                  <p>
                    {verifiedToUse.address_line_1}
                    {verifiedToUse.address_line_2 ? ` ${verifiedToUse.address_line_2}` : ''}
                  </p>
                  <p>
                    {verifiedToUse.admin_area_2}, {verifiedToUse.admin_area_1}{' '}
                    {verifiedToUse.postal_code}
                  </p>
                </div>
              </div>
              <label className="inline-flex items-center mt-2">
	                <input
	                  id="useVerifiedPrimaryAddress"
	                  type="checkbox"
	                  className="form-checkbox"
	                  checked={useVerifiedToUse}
	                  onChange={() => setUseVerifiedToUse(v => !v)}
	                />
	                <span className="ml-2">Use USPS suggested primary address</span>
	              </label>
            </div>
          )}

          {shippingSuggestion && (
            <div className="mb-6">
              <h4 className="font-semibold mb-2">Shipping Address</h4>
              <div className="grid grid-cols-2 gap-4">
                {/* Original */}
                <div className="p-3 border rounded">
                  <p className="text-gray-600 text-sm">Original</p>
                  <p>
                    {shippingAddress.address_line_1}
                    {shippingAddress.address_line_2 ? ` ${shippingAddress.address_line_2}` : ''}
                  </p>
                  <p>
                    {shippingAddress.admin_area_2}, {shippingAddress.admin_area_1}{' '}
                    {shippingAddress.postal_code}
                  </p>
                </div>
                {/* Suggested (comes from verifiedShipping or, if sameInitial, from verifiedToUse) */}
                <div className="p-3 border rounded">
                  <p className="text-gray-600 text-sm">USPS Suggested</p>
                  <p>
                    {shippingSuggestion.address_line_1}
                    {shippingSuggestion.address_line_2 ? ` ${shippingSuggestion.address_line_2}` : ''}
                  </p>
                  <p>
                    {shippingSuggestion.admin_area_2}, {shippingSuggestion.admin_area_1}{' '}
                    {shippingSuggestion.postal_code}
                  </p>
                </div>
              </div>
              <label className="inline-flex items-center mt-2">
	                <input
	                  id="useVerifiedShippingAddress"
	                  type="checkbox"
	                  className="form-checkbox"
	                  checked={useVerifiedShipping}
	                  onChange={() => setUseVerifiedShipping(v => !v)}
                />
                <span className="ml-2">Use USPS suggested shipping address</span>
              </label>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-between md:justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  // Mark address as verified to prevent re-verification loop
                  setAddressVerified(true);
                  setShowVerifyModal(false);
                  finalizePayPalOrder();
              }}
              className="w-full sm:w-1/2 md:w-1/3 px-4 py-2 border rounded"
            >
                Use original
              </button>
              <button
                type="button"
                onClick={handleModalConfirm}
                className="w-full sm:w-1/2 md:w-1/3 px-4 py-2 bg-blue-600 text-white rounded"
              >
                Use suggested
              </button>
          </div>
        </div>
      </div>
    );
  };


  return (
    <div id="paymentForm" className="flex flex-col flex-grow items-center justify-start w-full mt-0">
      <h2 className="text-xl font-semibold text-primary mb-3">
        {isOrder ? "Complete Your Payment" : "Complete Your Copay"}
      </h2>
      <TrustBadges height={40} className="mb-3" linkClassName="text-gray-600 hover:text-gray-800" />
      {isProcessing && <Spinner overlay={true} />}

      {!isProcessing && showError && (
        <div className="w-full max-w-3xl mx-auto mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Payment Error</h3>
              <div className="mt-1 text-sm text-red-700">
                <p>{processingError}</p>
                {paymentIssues.length > 0 && (
                  <ul className="mt-2 list-disc pl-5">
                    {paymentIssues.map((issue, index) => (
                      <li key={`${issue.type}-${index}`}>{issue.message}</li>
                    ))}
                  </ul>
                )}
                {processingError?.includes('phone') && (
                  <p className="mt-2">
                    Please update your profile with a valid phone number before trying again.
                  </p>
                )}
                {!actionMode && (processingError?.startsWith('Update address and phone before paying') || paymentIssues.some(issue => issue.type === 'profile')) && (
                  <Link
                    to="/profile?tab=contact"
                    className="inline-flex mt-3 px-4 py-2 bg-button text-white rounded hover:bg-button-light transition duration-200 font-semibold"
                  >
                    Update address and phone
                  </Link>
                )}
                {actionMode && (processingError?.startsWith('Update address and phone before paying') || paymentIssues.some(issue => issue.type === 'profile')) && (
                  <p className="mt-3">
                    Sign in to your RediMD account to update these details, then reopen the payment link.
                  </p>
                )}
                {!actionMode && paymentIssues.some(issue => issue.type === 'order' || issue.type === 'appointment') && (
                  <div className="mt-3 flex">
                    <Link
                      to={isOrder ? "/orders" : "/appointments"}
                      className="inline-flex justify-center px-4 py-2 bg-button text-white rounded hover:bg-button-light transition duration-200 font-semibold"
                    >
                      View {isOrder ? "Orders" : "Appointments"}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!isProcessing && showVerifyModal && !showReceipt && renderVerifyModal()}

      {!isProcessing && !showVerifyModal && !showReceipt && (
        <div className="w-full flex-col">
          {renderPayment()}
          {renderOrderSummary()}
        </div>
      )}

      {!isProcessing && !showVerifyModal && showReceipt && renderReceipt()}
    </div>
  );
}

export default PaymentForm;
